import {
  collection,
  getDocs,
  orderBy,
  query,
  Timestamp,
  where,
  limit,
  startAfter,
  documentId,
} from "firebase/firestore";
import db from "../../../Firebase";
import {
  PLANT_DISTRIBUTION_USER_LIMIT,
  MAX_IN_QUERY,
  COHORT_PAGE_SIZE,
} from "./constants";

const mapSnapshot = (snap) =>
  snap.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

const asTimestamp = (date) => (date ? Timestamp.fromDate(date) : null);

const handleError = (err) => {
  if (err?.code === "permission-denied") {
    return { error: "permission-denied", data: [] };
  }
  console.error("Firestore query failed", err);
  return { error: err?.code || "unknown", data: [] };
};

export const fetchEventsInRange = async (collectionName, start, end) => {
  try {
    const filters = [];
    const startTs = asTimestamp(start);
    const endTs = asTimestamp(end);
    if (startTs) filters.push(where("createdAt", ">=", startTs));
    if (endTs) filters.push(where("createdAt", "<", endTs));
    const ref = collection(db, collectionName);
    const q = query(ref, ...filters, orderBy("createdAt", "asc"));
    const snap = await getDocs(q);
    const rows = mapSnapshot(snap).map((row) => ({
      ...row,
      createdAt: row.createdAt?.toDate ? row.createdAt.toDate() : row.createdAt,
    }));
    return { data: rows };
  } catch (err) {
    return handleError(err);
  }
};

export const fetchCareActions = async (start, end) => {
  const [waterings, chats] = await Promise.all([
    fetchEventsInRange("Waterings", start, end),
    fetchEventsInRange("Chats", start, end),
  ]);
  return {
    waterings,
    chats,
  };
};

export const fetchUsersBefore = async (end, cap = PLANT_DISTRIBUTION_USER_LIMIT) => {
  try {
    const endTs = asTimestamp(end);
    const base = [orderBy("createdAt", "asc")];
    if (endTs) {
      base.push(where("createdAt", "<", endTs));
    }
    const ref = collection(db, "Users");
    const q = query(ref, ...base, limit(cap));
    const snap = await getDocs(q);
    const users = mapSnapshot(snap).map((row) => ({
      ...row,
      createdAt: row.createdAt?.toDate ? row.createdAt.toDate() : row.createdAt,
    }));
    return { data: users, truncated: snap.size >= cap };
  } catch (err) {
    return handleError(err);
  }
};

export const fetchUsersInRange = async (start, end, pageSize = COHORT_PAGE_SIZE) => {
  try {
    const filters = [orderBy("createdAt", "asc")];
    const startTs = asTimestamp(start);
    const endTs = asTimestamp(end);
    if (startTs) filters.push(where("createdAt", ">=", startTs));
    if (endTs) filters.push(where("createdAt", "<", endTs));
    const ref = collection(db, "Users");
    const q = query(ref, ...filters, limit(pageSize * MAX_IN_QUERY));
    const snap = await getDocs(q);
    const users = mapSnapshot(snap).map((row) => ({
      ...row,
      createdAt: row.createdAt?.toDate ? row.createdAt.toDate() : row.createdAt,
    }));
    return { data: users };
  } catch (err) {
    return handleError(err);
  }
};

export const fetchUsersWithPaging = async (start, end, pageSize = COHORT_PAGE_SIZE, cursor) => {
  try {
    const filters = [orderBy("createdAt", "asc")];
    const startTs = asTimestamp(start);
    const endTs = asTimestamp(end);
    if (startTs) filters.push(where("createdAt", ">=", startTs));
    if (endTs) filters.push(where("createdAt", "<", endTs));
    if (cursor) {
      filters.push(startAfter(cursor));
    }
    const ref = collection(db, "Users");
    const q = query(ref, ...filters, limit(pageSize));
    const snap = await getDocs(q);
    return { data: mapSnapshot(snap), cursor: snap.docs[snap.docs.length - 1] || null };
  } catch (err) {
    return handleError(err);
  }
};

export const fetchUsersByIds = async (ids = []) => {
  if (!ids || ids.length === 0) return { data: [] };
  try {
    const batches = [];
    for (let i = 0; i < ids.length; i += MAX_IN_QUERY) {
      const chunk = ids.slice(i, i + MAX_IN_QUERY);
      const q = query(collection(db, "Users"), where(documentId(), "in", chunk));
      batches.push(getDocs(q));
    }
    const results = await Promise.all(batches);
    const users = results.flatMap((snap) =>
      mapSnapshot(snap).map((row) => ({
        ...row,
        createdAt: row.createdAt?.toDate ? row.createdAt.toDate() : row.createdAt,
      }))
    );
    return { data: users };
  } catch (err) {
    return handleError(err);
  }
};
